import type { LocationQueryRaw, RouteLocationNamedRaw } from "vue-router";

// Maps CMS / API resource types to Nuxt route locations. Replaces the
// $slugBuilder + $pdSlugBuilder plugins with a pure, tree-shakeable util.
// Discipline / parentSlug / course are passed explicitly by callers — no
// implicit fallbacks via useRoute() like the old plugins had.

// Removed in 2026-05-07 cleanup after verifying against the Laravel backend
// (juilliardcreativeclassroom): `core-works`, `teachables`, `unit_sequences`.
// None are emitted as `type` / `resource_type` values by the API resources —
// they only appear as Eloquent relation names, Algolia index names, or JSON
// collection keys. If a runtime error surfaces an unknown ResourceType value
// matching one of these, the backend has changed — re-add the alias and
// re-grep app/Http/Resources/ to find the new emission site.
export type ResourceType =
   // teachable detail pages — all CMS aliases route to `teachable`
   | "teachable"
   | "practitioner"
   | "skill"
   | "lesson_plan"
   | "tls"
   // teachables nested under planning tool / core work
   | "planning-tool-teachable"
   | "core-work-teachable"
   | "artistic_insight"
   // core works
   | "core-work"
   // unit sequences
   | "planning-tool"
   // PD courses
   | "pd"
   | "pd-section"
   | "pd-takeaway";

interface RouteForResourceOpts {
   type: ResourceType;
   slug: string;
   discipline?: string | string[];
   parentSlug?: string;
   query?: LocationQueryRaw;
   course?: string | string[];
}

type Builder = (
   slug: string,
   rest: Omit<RouteForResourceOpts, "type" | "slug">,
) => RouteLocationNamedRaw;

const teachableRoute: Builder = (slug, { discipline, query }) => ({
   name: "teachable",
   params: { slug, discipline },
   query,
});

const builders: Record<ResourceType, Builder> = {
   teachable: teachableRoute,
   practitioner: teachableRoute,
   skill: teachableRoute,
   lesson_plan: teachableRoute,
   tls: teachableRoute,

   "planning-tool-teachable": (slug, { discipline, parentSlug, query }) => ({
      name: "planning-tool-teachable",
      params: { slug, discipline, planningTool: parentSlug },
      query,
   }),
   "core-work-teachable": (slug, { discipline, parentSlug, query }) => ({
      name: "core-work-teachable",
      params: { slug, discipline, corework: parentSlug },
      query,
   }),
   artistic_insight: (slug, { discipline, parentSlug, query }) => ({
      name: "artistic-insight",
      params: { slug, discipline, corework: parentSlug },
      query,
   }),

   "core-work": (slug, { discipline, query }) => ({
      name: "core-work",
      params: { slug, discipline },
      query,
   }),

   "planning-tool": (slug, { discipline, query }) => ({
      name: "unit",
      params: { slug, discipline },
      query,
   }),

   pd: (slug, { query }) => ({
      name: "pd-course",
      params: { course: slug ?? "" },
      query,
   }),
   "pd-section": (slug, { course, query }) => ({
      name: "pd-section",
      params: { slug, course },
      query,
   }),
   "pd-takeaway": (slug, { query }) => ({
      name: "pd-course-takeaway",
      params: { course: slug },
      query,
   }),
};

export function routeForResource(
   opts: RouteForResourceOpts,
): RouteLocationNamedRaw | undefined {
   const { type, slug, ...rest } = opts;
   const builder = builders[type];
   return builder ? builder(slug, rest) : undefined;
}
